import { Controller } from "@hotwired/stimulus"
import { Picker } from "emoji-picker-element"
import { csrfToken } from "../utils/csrf"
import { announceReactionReply } from "../utils/reaction_reply"

// Shared emoji reaction picker (shared/_reaction_picker.html.slim): a quick-react
// popover (Reactable::EMOJIS) plus a "more" button that lazily upgrades to a
// full Slack-style <emoji-picker> (search, categories, skin tones), backed by a
// locally-served emoji-data.json (no CDN fetch at runtime — see package.json's
// build:emoji-data step).
//
// Two ways a surface can use this:
//   - urlValue present (feedback/reflections/DM chat): choosing an emoji POSTs
//     FormData{emoji} to it with the turbo-stream Accept header, same pattern
//     as chat_reactions_controller#_send. The resulting broadcast/Turbo-stream
//     response reconciles the chip row — we never mutate chips ourselves.
//   - urlValue absent (Watch Party, cable-driven): we only dispatch
//     "emoji-picker:picked" and let an ancestor controller (watch-party) react
//     to the emoji itself over its own transport.
// Either way we ALWAYS dispatch picked so a listener can hook in regardless.
export default class extends Controller {
    static targets = ["menu", "full"]
    static values = { url: String, dataSource: String }

    connect() {
        this._onOutside = (e) => {
            if (this.element.contains(e.target)) return
            this.close()
        }
        this._onKeydown = (e) => {
            if (e.key === "Escape") this.close()
        }
        document.addEventListener("click", this._onOutside)
        document.addEventListener("keydown", this._onKeydown)
    }

    disconnect() {
        document.removeEventListener("click", this._onOutside)
        document.removeEventListener("keydown", this._onKeydown)
        if (this._onThemeChange) document.removeEventListener("emailpa:themechange", this._onThemeChange)
        if (this.picker && this.picker.isConnected) this.picker.remove()
    }

    // Trigger button: open/close the quick-react popover.
    toggle(e) {
        e.stopPropagation()
        if (!this.hasMenuTarget) return
        const opening = this.menuTarget.hidden
        this.close()
        if (opening) {
            this.menuTarget.hidden = false
            this._position(this.menuTarget)
        }
    }

    // A quick-react option: pick it directly.
    pick(e) {
        const emoji = e.currentTarget.dataset.emoji
        this.close()
        if (emoji) this._choose(emoji)
    }

    // "More": lazily build the full <emoji-picker> (once) and show it in place
    // of the quick popover.
    more(e) {
        e.stopPropagation()
        if (!this.hasFullTarget) return
        this._ensurePicker()
        if (this.hasMenuTarget) this.menuTarget.hidden = true
        this.fullTarget.hidden = false
        this._position(this.fullTarget, { width: "min(90vw, 330px)", height: "350px" })
    }

    // Announces "emoji-picker:closed" only when something was actually open,
    // so a poller holding a re-render until the picker is done (see
    // assistant_poll_controller.js) isn't woken by every stray outside click.
    close() {
        const wasOpen = (this.hasMenuTarget && !this.menuTarget.hidden) || (this.hasFullTarget && !this.fullTarget.hidden)
        if (this.hasMenuTarget) this.menuTarget.hidden = true
        if (this.hasFullTarget) this.fullTarget.hidden = true
        if (wasOpen) this.dispatch("closed", { bubbles: true })
    }

    _ensurePicker() {
        if (this.picker) return
        this.picker = new Picker({ dataSource: this.dataSourceValue })
        this.picker.classList.add(this._prefersDark() ? "dark" : "light")
        this.picker.addEventListener("emoji-click", (event) => {
            const detail = event.detail || {}
            const emoji = detail.unicode || (detail.emoji && detail.emoji.unicode)
            this.close()
            if (emoji) this._choose(emoji)
        })
        this._onThemeChange = (event) => {
            if (!this.picker) return
            const dark = event.detail.theme === "dark"
            this.picker.classList.toggle("dark", dark)
            this.picker.classList.toggle("light", !dark)
        }
        document.addEventListener("emailpa:themechange", this._onThemeChange)
        this.fullTarget.appendChild(this.picker)
    }

    _prefersDark() {
        return document.documentElement.getAttribute("data-bs-theme") === "dark"
    }

    _choose(emoji) {
        if (this.urlValue) this._send(emoji)
        this.dispatch("picked", { detail: { emoji }, bubbles: true })
    }

    // POST-only endpoints (feedback, reflections, reflection comments) render
    // and return the replacement turbo-stream directly, since there's no
    // ActionCable broadcast backing them — apply it ourselves, the same way
    // Turbo Drive would have for the old real <form> submission this replaces.
    // Cable-backed endpoints (DM/channel reactions) respond 204 No Content
    // (the chip row instead arrives over their live broadcast stream), so
    // `html` is empty there and this is a no-op — same as the previous
    // chat-reactions fetch-and-discard behavior.
    _send(emoji) {
        const body = new FormData()
        body.set("emoji", emoji)
        fetch(this.urlValue, {
            method: "POST",
            headers: { Accept: "text/vnd.turbo-stream.html", "X-CSRF-Token": csrfToken() },
            body
        })
            .then((response) => {
                announceReactionReply(response)
                return response.text()
            })
            .then((html) => {
                if (html && window.Turbo) window.Turbo.renderStreamMessage(html)
            })
            .catch(() => {})
    }

    // Flush `panel`'s left edge against the trigger, then clamp horizontally
    // so it never spills past the viewport edge. `size` optionally sets
    // width/height first (the full picker panel needs an explicit box before
    // it can be measured). Flush-left (rather than centered) means the panel
    // never sits further left than the trigger itself — important on pages
    // with a persistent left nav rail, where a centered popover over a
    // trigger near the rail would render partly underneath it.
    _position(panel, size) {
        if (size) {
            panel.style.width = size.width
            panel.style.height = size.height
        }
        const wrap = panel.offsetParent || this.element
        if (!wrap) return

        panel.style.left = "0px"
        const panelWidth = panel.offsetWidth
        const wrapRect = wrap.getBoundingClientRect()
        const margin = 8
        const viewport = document.documentElement.clientWidth

        let left = 0
        let viewportLeft = wrapRect.left + left
        if (viewportLeft + panelWidth > viewport - margin) {
            left -= viewportLeft + panelWidth - (viewport - margin)
            viewportLeft = wrapRect.left + left
        }
        if (viewportLeft < margin) left += margin - viewportLeft
        panel.style.left = `${Math.round(left)}px`

        // Vertical: the SCSS anchors both panels below the trigger. When that
        // would spill past the viewport bottom (e.g. a message near the fold)
        // and there's more room above the trigger than below it, flip the
        // panel to open upward instead.
        panel.style.top = ""
        panel.style.bottom = ""
        const rect = panel.getBoundingClientRect()
        const viewportHeight = document.documentElement.clientHeight
        if (rect.bottom > viewportHeight - margin) {
            const anchorRect = this.element.getBoundingClientRect()
            if (anchorRect.top > viewportHeight - anchorRect.bottom) {
                panel.style.top = "auto"
                panel.style.bottom = "calc(100% + 0.4rem)"
            }
        }
    }
}
